import { useState, useEffect, useContext } from 'react';
import {
    Grid,
    Paper,
    List,
    ListItem,
    ListItemText,
    Box,
    ListItemSecondaryAction,
    IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog, SttFormText } from '../../../sporttia/all';
import { subsetObject } from '../../../lib/utils';

export default function BookingSelectPupilsDialog({
    open = false,
    occupants,
    sc,
    onClose,
    onAddOccupant,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [allPupils, setAllPupils] = useState<any>();
    const [availableUsers, setAvailableUsers] = useState<any>();
    const [newUser, setNewUser] = useState<any>({});

    /**
     * Load pupils
     */
    const loadPupils = () => {
        cxt.api('GET', `/scs/${sc.id}/pupils`, {
            params: {
                page: 1,
                rows: 100,
            },
            success: (r: any) => {
                setAllPupils(r.rows);
            },
        });
    };

    /**
     * Generate available users
     */
    useEffect(() => {
        if (allPupils && cxt.user) {
            const allPeople = [
                {
                    ...subsetObject(cxt.user, 'id,name,fullName'),
                    checked: false,
                },
                ...allPupils.map((pupil: any) => ({
                    ...pupil,
                    checked: false,
                })),
            ];

            // Only show who is not already in the booking
            setAvailableUsers(
                allPeople.filter(
                    (user: any) =>
                        user &&
                        user.id &&
                        !occupants.find(
                            (occupant: any) =>
                                occupant.user && occupant.user.id === user.id,
                        ),
                ),
            );
        }
    }, [occupants, allPupils]);

    /**
     * Init
     */
    useEffect(() => {
        if (sc) loadPupils();
    }, [sc]);

    /**
     * Handle new user
     */
    const handleNewUser = ({ name, value }: any) => {
        setNewUser({ ...newUser, [name]: value });
    };

    /**
     * Create pupil
     */
    const createPupil = () => {
        cxt.api('POST', `/my/scs/${sc.id}/pupils`, {
            params: newUser,
            success: (r: any) => {
                cxt.showMessage('S', t('messages.created'));

                // Add user to the booking
                onAddOccupant({
                    name: r.user.fullName,
                    user: r.user,
                });

                // Clear new user
                setNewUser({});
            },
        });
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('booking.pupils')}
            open={open}
            onClose={onClose}
            content={
                <>
                    {availableUsers && availableUsers.length > 0 && (
                        <Paper>
                            <List>
                                {availableUsers.map(
                                    (user: any, idxUser: any) => (
                                        <ListItem key={idxUser}>
                                            <ListItemText
                                                primary={user.fullName}
                                            />

                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => {
                                                        onAddOccupant({
                                                            name: user.fullName,
                                                            user,
                                                        });
                                                        cxt.showMessage(
                                                            'S',
                                                            t('messages.added'),
                                                        );
                                                    }}
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ),
                                )}
                            </List>
                        </Paper>
                    )}

                    <Box mt={4}>{t('booking.youCanAddNoRegistered')}</Box>
                    <Box mt={1}>
                        <Grid container spacing={3}>
                            <SttFormText
                                grid
                                md={12}
                                sm={12}
                                xs={12}
                                caption={t('labels.name')}
                                name="name"
                                defVal={newUser.name}
                                onChange={handleNewUser}
                            />

                            <SttFormText
                                grid
                                md={6}
                                sm={6}
                                xs={12}
                                caption={`${t('labels.surname')} 1`}
                                name="surname1"
                                defVal={newUser.surname1}
                                onChange={handleNewUser}
                            />

                            <SttFormText
                                grid
                                md={6}
                                sm={6}
                                xs={12}
                                caption={`${t('labels.surname')} 2`}
                                name="surname2"
                                defVal={newUser.surname2}
                                onChange={handleNewUser}
                            />
                        </Grid>
                    </Box>
                </>
            }
            buttons={[
                {
                    caption: t('booking.addUser'),
                    icon: <PersonAddIcon />,
                    onClick: () => createPupil(),
                },
            ]}
        />
    );
}
